import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import client from "@/api/client";
import { Clock, ShieldAlert, Lock, User, Compass, LifeBuoy } from "lucide-react";

const LOCKED = ["dataRoom.navTitle", "dashboard.programTitle", "dashboard.myBookings", "memberCalendar.title"];

/**
 * PendingApprovalScreen — the only thing an unapproved account sees. It is
 * deliberately read-only: their profile summary and the journey they saved,
 * nothing else.
 */
export function PendingApprovalScreen() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const rejected = user?.status === "rejected";
  const [profile, setProfile] = useState(null);
  const [journey, setJourney] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await client.get("/profile/me");
      setProfile(res.data?.profile || res.data || null);
    } catch { setProfile(null); }
    try {
      const res = await client.get("/journey/me");
      setJourney(res.data?.session || null);
    } catch { setJourney(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const journeySlug = (journey?.business_type_slug || user?.journey?.business_type_slug || "").replace(/-/g, " ");
  const programCount = (journey?.selected_template_ids || user?.journey?.selected_template_ids || []).length;

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10 sm:py-16 space-y-6" data-testid="pending-approval-screen">
      <Card
        className={`relative overflow-hidden rounded-3xl border p-6 sm:p-8 ${rejected ? "border-rose-500/40 bg-rose-500/5" : "border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-background to-transparent"}`}
        data-testid={rejected ? "pending-screen-rejected" : "pending-screen-awaiting"}
      >
        <div className="flex items-start gap-4">
          <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${rejected ? "bg-rose-500/15 text-rose-600" : "bg-amber-500/15 text-amber-600"}`}>
            {rejected ? <ShieldAlert className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
          </span>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">
              {t("pendingScreen.eyebrow", { defaultValue: "Account status" })}
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1.5">
              {rejected
                ? t("pendingScreen.rejectedTitle", { defaultValue: "Your account was not approved" })
                : t("pendingScreen.title", { defaultValue: "Awaiting approval" })}
            </h1>
            <p className="text-sm text-muted-foreground mt-2.5 max-w-xl">
              {rejected
                ? t("pendingScreen.rejectedBody", { defaultValue: "Reach out to the team and we'll walk you through the decision." })
                : t("pendingScreen.body", { defaultValue: "We've saved everything you entered. The team reviews new accounts daily — usually within 24 hours — and your workspace unlocks the moment you're approved." })}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" className="rounded-xl gap-2" onClick={() => navigate("/contact")} data-testid="pending-contact-cta">
                <LifeBuoy className="h-4 w-4" /> {t("pendingScreen.contact", { defaultValue: "Contact the team" })}
              </Button>
              {journeySlug && (
                <Button variant="ghost" className="rounded-xl gap-2" onClick={() => navigate(`/journey?type=${(journey?.business_type_slug || user?.journey?.business_type_slug)}`)} data-testid="pending-edit-journey">
                  <Compass className="h-4 w-4" /> {t("pendingScreen.editJourney", { defaultValue: "Review my journey" })}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2">
        <Card className="rounded-2xl border border-border p-5 space-y-3" data-testid="pending-profile-summary">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-primary" />
            <h2 className="font-display font-semibold text-base">
              {t("pendingScreen.profileTitle", { defaultValue: "Your details" })}
            </h2>
            <Badge variant="outline" className="ms-auto rounded-full text-[10px]">
              {t("pendingScreen.readOnly", { defaultValue: "Read only" })}
            </Badge>
          </div>
          <dl className="space-y-2 text-sm">
            {[
              ["pendingScreen.name", user?.name],
              ["roles.label", t(`roles.${user?.role}`, { defaultValue: user?.role })],
              ["pendingScreen.headline", profile?.headline],
              ["pendingScreen.company", profile?.company_name],
              ["pendingScreen.region", profile?.region],
            ].map(([key, value]) => (
              <div key={key} className="flex items-start gap-3">
                <dt className="w-28 shrink-0 text-[11px] uppercase tracking-widest text-muted-foreground">
                  {t(key, { defaultValue: key.split(".").pop() })}
                </dt>
                <dd className="min-w-0 flex-1 truncate">{value || "—"}</dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card className="rounded-2xl border border-border p-5 space-y-3" data-testid="pending-journey-summary">
          <div className="flex items-center gap-2">
            <Compass className="h-4 w-4 text-primary" />
            <h2 className="font-display font-semibold text-base">
              {t("journey.linkTitle", { defaultValue: "Your saved journey" })}
            </h2>
          </div>
          {journeySlug ? (
            <div className="space-y-2 text-sm">
              <div className="rounded-xl border border-border p-3">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("journey.businessType")}</p>
                <p className="font-medium capitalize mt-1">{journeySlug}</p>
              </div>
              <div className="rounded-xl border border-border p-3">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  {t("pendingScreen.programsSelected", { defaultValue: "Programs selected" })}
                </p>
                <p className="font-medium mt-1 font-mono">{programCount}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t("pendingScreen.noJourney", { defaultValue: "You didn't save a journey — you can start one any time." })}
            </p>
          )}
        </Card>
      </div>

      <Card className="rounded-2xl border border-dashed border-border p-5" data-testid="pending-locked-list">
        <div className="flex items-center gap-2 mb-3">
          <Lock className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display font-semibold text-base">
            {t("pendingScreen.lockedTitle", { defaultValue: "Unlocks after approval" })}
          </h2>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          {LOCKED.map((key) => (
            <div key={key} className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2 flex items-center gap-2 opacity-70" data-testid={`pending-locked-${key.split(".").pop()}`}>
              <Lock className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{t(key, { defaultValue: key.split(".").pop() })}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export default PendingApprovalScreen;
